import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import Input from '../components/atoms/Input';
import SessionManager from '../utils/sessionManager';
import DemoLeadsManager from '../components/organisms/DemoLeadsManager';
import CreditManagement from '../components/organisms/CreditManagement';
import { API_URL } from '../config';

// Build headers including JWT Authorization for super_admin role
const adminAuthHeaders = (extra = {}) => {
  const session = SessionManager.getSession('super_admin') || SessionManager.getCurrentSession();
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [verificationDropdownOpen, setVerificationDropdownOpen] = useState(false);
  const [stats, setStats] = useState({
    hr_companies: 0,
    course_providers: 0,
    saas_providers: 0,
    active_api_keys: 0,
    total_jobs: 0
  });
  const [revenueStats, setRevenueStats] = useState({
    total_revenue: 0,
    course_revenue: 0,
    saas_revenue: 0,
    job_posting_revenue: 0,
    total_commission: 0,
    transactions_count: 0,
    period: 'all'
  });
  const [apiKeys, setApiKeys] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Token Usage State
  const [tokenStats, setTokenStats] = useState({
    total_tokens_today: 0,
    total_cost_usd_today: 0,
    total_cost_inr_today: 0,
    total_tokens_month: 0,
    total_cost_usd_month: 0,
    total_cost_inr_month: 0,
    interview_usage: { tokens: 0, cost_usd: 0, cost_inr: 0 },
    training_usage: { tokens: 0, cost_usd: 0, cost_inr: 0 },
    resume_enhance_usage: { tokens: 0, cost_usd: 0, cost_inr: 0 },
    exchange_rate: 90
  });
  const [tokenDetails, setTokenDetails] = useState([]);
  const [systemSettings, setSystemSettings] = useState({});
  const [showExchangeRateForm, setShowExchangeRateForm] = useState(false);
  const [newExchangeRate, setNewExchangeRate] = useState('');
  
  // Management State
  const [hrCompanies, setHrCompanies] = useState([]);
  const [courseProviders, setCourseProviders] = useState([]);
  const [saasProviders, setSaasProviders] = useState([]);
  const [managementLoading, setManagementLoading] = useState(false);
  
  // API Key Form
  const [showApiKeyForm, setShowApiKeyForm] = useState(false);
  const [apiKeyForm, setApiKeyForm] = useState({
    company_name: '',
    cin_number: '',
    key_type: 'hr',
    contact_email: '',
    contact_phone: ''
  });
  const [newApiKey, setNewApiKey] = useState(null);
  const [visibleApiKeys, setVisibleApiKeys] = useState({});
  
  // HR Account Form
  const [showHRForm, setShowHRForm] = useState(false);
  const [hrForm, setHrForm] = useState({
    username: '',
    email: '',
    password: '',
    contact_name: '',
    company_name: '',
    company_website: '',
    phone_country_code: '+91',
    phone_number: ''
  });
  const [newHRAccount, setNewHRAccount] = useState(null);

  // Phone Test State
  const [phoneTestNumber, setPhoneTestNumber] = useState('+919176186062'); // Default test number
  const [phoneTestLoading, setPhoneTestLoading] = useState(false);
  const [phoneTestResult, setPhoneTestResult] = useState(null);
  const [phoneSystemStatus, setPhoneSystemStatus] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US'); // Default language

  const session = SessionManager.getCurrentSession();
  const adminId = session?.user_id;

  useEffect(() => {
    // Check if user is super admin using SessionManager
    const session = SessionManager.getCurrentSession();
    if (!session || session.role !== 'super_admin') {
      navigate('/');
      return;
    }
    
    loadDashboardData();
  }, [navigate]);

  // Load management data when switching to management tabs
  useEffect(() => {
    if (activeTab === 'hr-management' || activeTab === 'course-management' || activeTab === 'saas-management') {
      loadManagementData();
    }
  }, [activeTab]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load platform stats
      const statsRes = await fetch(`/api/super-admin/stats/overview`, { headers: adminAuthHeaders() });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Load revenue stats
      const revenueRes = await fetch(`/api/super-admin/revenue/stats?period=all`, { headers: adminAuthHeaders() });
      if (revenueRes.ok) {
        const revenueData = await revenueRes.json();
        setRevenueStats(revenueData);
      }

      // Load API keys
      const keysRes = await fetch(`/api/super-admin/api-keys`, { headers: adminAuthHeaders() });
      if (keysRes.ok) {
        const keysData = await keysRes.json();
        setApiKeys(keysData);
      }

      // Load recent transactions
      const txRes = await fetch(`/api/super-admin/revenue/transactions?limit=20`, { headers: adminAuthHeaders() });
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData);
      }

      // Load token usage stats
      const tokenStatsRes = await fetch(`/api/token-usage/stats`);
      if (tokenStatsRes.ok) {
        const tokenStatsData = await tokenStatsRes.json();
        setTokenStats(tokenStatsData);
      }

      // Load token usage details
      const tokenDetailsRes = await fetch(`/api/token-usage/details?limit=50`);
      if (tokenDetailsRes.ok) {
        const tokenDetailsData = await tokenDetailsRes.json();
        setTokenDetails(tokenDetailsData);
      }

      // Load system settings
      const settingsRes = await fetch(`/api/token-usage/settings`);
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSystemSettings(settingsData);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/super-admin/api-keys`, {
        method: 'POST',
        headers: { ...adminAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(apiKeyForm)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('=== API Key Response ===');
        console.log('Full response:', data);
        console.log('Has provider_credentials?', !!data.provider_credentials);
        console.log('Key type:', data.api_key_info?.key_type);
        
        setNewApiKey(data);
        
        // Reload the full list to get provider credentials
        loadDashboardData();
        
        setApiKeyForm({
          company_name: '',
          cin_number: '',
          key_type: 'hr',
          contact_email: '',
          contact_phone: ''
        });
        // Don't close form yet - let user see the credentials
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Unknown error';
        alert(`❌ Failed to create API key:\n\n${errorMessage}`);
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Error creating API key');
    }
  };

  const handleCreateHRAccount = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/super-admin/create-hr-account`, {
        method: 'POST',
        headers: { ...adminAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(hrForm)
      });

      if (response.ok) {
        const data = await response.json();
        setNewHRAccount(data);
        setHrForm({
          username: '',
          email: '',
          password: '',
          contact_name: '',
          company_name: '',
          company_website: '',
          phone_country_code: '+91',
          phone_number: ''
        });
        loadDashboardData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create HR account');
      }
    } catch (error) {
      console.error('Error creating HR account:', error);
      alert('Error creating HR account');
    }
  };

  const handleToggleApiKey = async (keyId) => {
    try {
      const response = await fetch(`/api/super-admin/api-keys/${keyId}/toggle`, { method: 'PATCH', headers: adminAuthHeaders() });

      if (response.ok) {
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error toggling API key:', error);
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;
    
    try {
      const response = await fetch(`/api/super-admin/api-keys/${keyId}`, { method: 'DELETE', headers: adminAuthHeaders() });

      if (response.ok) {
        setApiKeys(apiKeys.filter(k => k.id !== keyId));
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
    }
  };

  const handleUpdateExchangeRate = async (e) => {
    e.preventDefault();
    try {
      const rate = parseFloat(newExchangeRate);
      if (rate <= 0) {
        alert('Exchange rate must be positive');
        return;
      }

      const response = await fetch(`/api/token-usage/exchange-rate?new_rate=${rate}&admin_id=${adminId}`, {
        method: 'PUT'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✓ ${data.message}`);
        setNewExchangeRate('');
        setShowExchangeRateForm(false);
        loadDashboardData(); // Reload to get updated stats
      } else {
        const error = await response.json();
        alert(`❌ ${error.detail}`);
      }
    } catch (error) {
      console.error('Error updating exchange rate:', error);
      alert('Error updating exchange rate');
    }
  };

  const loadManagementData = async () => {
    setManagementLoading(true);
    try {
      console.log('Loading management data...');
      
      // Load HR Companies
      const hrRes = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/super-admin/hr-companies`, {
        credentials: 'include',
        headers: adminAuthHeaders(),
      });
      if (hrRes.ok) {
        const hrData = await hrRes.json();
        console.log('HR Companies loaded:', hrData);
        setHrCompanies(hrData);
      } else {
        console.error('Failed to load HR companies:', hrRes.status);
        const errorText = await hrRes.text();
        console.error('Error details:', errorText);
      }

      // Load Course Providers
      const courseRes = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/super-admin/course-providers`, {
        credentials: 'include',
        headers: adminAuthHeaders(),
      });
      if (courseRes.ok) {
        const courseData = await courseRes.json();
        console.log('Course Providers loaded:', courseData);
        setCourseProviders(courseData);
      } else {
        console.error('Failed to load course providers:', courseRes.status);
        const errorText = await courseRes.text();
        console.error('Error details:', errorText);
      }

      // Load SaaS Providers
      const saasRes = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/super-admin/saas-providers`, {
        credentials: 'include',
        headers: adminAuthHeaders(),
      });
      if (saasRes.ok) {
        const saasData = await saasRes.json();
        console.log('SaaS Providers loaded:', saasData);
        setSaasProviders(saasData);
      } else {
        console.error('Failed to load SaaS providers:', saasRes.status);
        const errorText = await saasRes.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      console.error('Error loading management data:', error);
    } finally {
      setManagementLoading(false);
    }
  };

  const handleToggleUserAccess = async (userId, userType, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'disable' : 'enable'} access for this ${userType}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/super-admin/toggle-access`, {
        method: 'POST',
        headers: { ...adminAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          user_type: userType,
          action: currentStatus ? 'disable' : 'enable'
        })
      });

      if (response.ok) {
        alert(`✓ User access ${currentStatus ? 'disabled' : 'enabled'} successfully`);
        loadManagementData(); // Reload data
      } else {
        const error = await response.json();
        alert(`❌ ${error.detail || 'Failed to update user access'}`);
      }
    } catch (error) {
      console.error('Error toggling user access:', error);
      alert('Error updating user access');
    }
  };

  const handleDeleteUser = async (userId, userType) => {
    if (!window.confirm(`Are you sure you want to permanently delete this ${userType}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/super-admin/delete-user`, {
        method: 'DELETE',
        headers: { ...adminAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          user_type: userType
        })
      });

      if (response.ok) {
        alert(`✓ ${userType} deleted successfully`);
        loadManagementData(); // Reload data
        loadDashboardData(); // Reload stats
      } else {
        const error = await response.json();
        alert(`❌ ${error.detail || 'Failed to delete user'}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user');
    }
  };

  const handleCleanupOrphanedContent = async () => {
    if (!window.confirm('Are you sure you want to clean up orphaned content? This will remove jobs, courses, and SaaS products from deleted providers.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/super-admin/cleanup-orphaned-content`, {
        method: 'POST',
        headers: { ...adminAuthHeaders(), 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        const { summary } = result;
        
        let message = '✅ Cleanup completed successfully!\n\n';
        message += `• Orphaned jobs removed: ${summary.orphaned_jobs}\n`;
        message += `• Orphaned courses removed: ${summary.orphaned_courses}\n`;
        message += `• Orphaned SaaS products removed: ${summary.orphaned_saas_products}\n`;
        message += `\nTotal items cleaned: ${summary.total_deleted}`;
        
        alert(message);
        loadDashboardData(); // Reload stats
      } else {
        const error = await response.json();
        alert(`❌ ${error.detail || 'Failed to cleanup orphaned content'}`);
      }
    } catch (error) {
      console.error('Error cleaning up orphaned content:', error);
      alert('Error cleaning up orphaned content');
    } finally {
      setLoading(false);
    }
  };

  // Phone Test Functions
  const loadPhoneSystemStatus = async () => {
    try {
      const response = await fetch(`${API_URL.replace('/api', '')}/api/phone/status`);
      if (response.ok) {
        const status = await response.json();
        setPhoneSystemStatus(status);
      }
    } catch (error) {
      console.error('Error loading phone system status:', error);
    }
  };

  const handleTestPhoneCall = async () => {
    if (!phoneTestNumber) {
      alert('Please enter a phone number to test');
      return;
    }

    setPhoneTestLoading(true);
    setPhoneTestResult(null);

    try {
      const response = await fetch(`${API_URL.replace('/api', '')}/api/phone/test-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to_number: phoneTestNumber,
          custom_prompt: customPrompt,
          language: selectedLanguage // Add language to request
        })
      });

      const result = await response.json();
      setPhoneTestResult(result);

      if (result.success) {
        let message = `✅ Test call initiated successfully!\n\nCall SID: ${result.call_sid}\nCalling: ${result.to}\nFrom: ${result.from}\nLanguage: ${selectedLanguage}`;
        if (result.custom_prompt && result.custom_prompt !== 'Using default prompt') {
          message += `\nCustom Prompt: Applied`;
        }
        message += `\n\nYour phone should ring shortly!`;
        alert(message);
      } else {
        alert(`❌ Failed to make test call:\n${result.error}`);
      }
    } catch (error) {
      console.error('Error making test call:', error);
      setPhoneTestResult({ success: false, error: error.message });
      alert(`❌ Error making test call: ${error.message}`);
    } finally {
      setPhoneTestLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="flex">
        {/* Sidebar Navigation */}
        <div className="w-72 bg-white shadow-lg min-h-screen sticky top-0">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <Icon name="admin_panel_settings" className="text-white text-2xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Admin Panel</h2>
                <p className="text-xs text-gray-500">Super Admin</p>
              </div>
            </div>
          </div>

          <nav className="p-4 space-y-1">
            {/* Overview */}
            <button
              onClick={() => setActiveTab('overview')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'overview'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="dashboard" className="text-xl" />
              <span>Overview</span>
            </button>

            {/* Management Section */}
            <div className="pt-4 pb-2">
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Management</p>
            </div>
            
            <button
              onClick={() => {
                setActiveTab('hr-management');
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'hr-management'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="work" className="text-xl" />
              <span>HR Companies</span>
            </button>

            <button
              onClick={() => {
                setActiveTab('course-management');
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'course-management'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="school" className="text-xl" />
              <span>Course Providers</span>
            </button>

            <button
              onClick={() => {
                setActiveTab('saas-management');
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'saas-management'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="cloud" className="text-xl" />
              <span>SaaS Providers</span>
            </button>

            {/* API & Integration */}
            <div className="pt-4 pb-2">
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">API & Integration</p>
            </div>

            <button
              onClick={() => setActiveTab('api-keys')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'api-keys'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="key" className="text-xl" />
              <span>API Keys</span>
            </button>

            {/* Financial */}
            <div className="pt-4 pb-2">
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Financial</p>
            </div>

            <button
              onClick={() => setActiveTab('transactions')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'transactions'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="receipt_long" className="text-xl" />
              <span>Transactions</span>
            </button>

            <button
              onClick={() => setActiveTab('token-usage')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'token-usage'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="analytics" className="text-xl" />
              <span>Token Usage</span>
            </button>

            <button
              onClick={() => setActiveTab('credits')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'credits'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="account_balance_wallet" className="text-xl" />
              <span>Credits</span>
            </button>

            <button
              onClick={() => navigate('/super-admin/payments')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
            >
              <Icon name="payments" className="text-xl" />
              <span>Payments</span>
            </button>

            <button
              onClick={() => navigate('/super-admin/packages')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
            >
              <Icon name="card_giftcard" className="text-xl" />
              <span>Packages</span>
            </button>

            {/* System */}
            <div className="pt-4 pb-2">
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">System</p>
            </div>

            <button
              onClick={() => navigate('/super-admin/data-analysis')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
            >
              <Icon name="query_stats" className="text-xl" />
              <span>Data Analysis</span>
            </button>

            {/* Verification with Dropdown */}
            <div className="relative">
              <button
                onClick={() => setVerificationDropdownOpen(!verificationDropdownOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                  verificationDropdownOpen ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon name="verified_user" className="text-xl" />
                  <span>Verification</span>
                </div>
                <Icon 
                  name={verificationDropdownOpen ? "expand_less" : "expand_more"} 
                  className={`text-xl transition-transform ${verificationDropdownOpen ? 'rotate-0' : 'rotate-0'}`} 
                />
              </button>
              
              {verificationDropdownOpen && (
                <div className="ml-8 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
                  <button
                    onClick={() => {
                      navigate('/super-admin/verification');
                      setVerificationDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-700 transition-all"
                  >
                    <Icon name="dashboard" className="text-lg" />
                    <span>Score Dashboard</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/super-admin/verification/candidates');
                      setVerificationDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-700 transition-all"
                  >
                    <Icon name="person_search" className="text-lg" />
                    <span>Candidate Search</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/super-admin/verification/settings');
                      setVerificationDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-700 transition-all"
                  >
                    <Icon name="settings" className="text-lg" />
                    <span>Settings</span>
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setActiveTab('demo-leads')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'demo-leads'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="people" className="text-xl" />
              <span>Demo Leads</span>
            </button>

            <button
              onClick={() => {
                setActiveTab('phone-test');
                loadPhoneSystemStatus();
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'phone-test'
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon name="phone" className="text-xl" />
              <span>Phone Test</span>
            </button>
          </nav>

          {/* User Info at Bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {session?.username?.charAt(0).toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{session?.username || 'aptirix'}</p>
                <p className="text-xs text-gray-500">Super Admin</p>
              </div>
              <button
                onClick={() => {
                  SessionManager.clearSession();
                  navigate('/');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition"
              >
                <Icon name="logout" className="text-xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              {activeTab === 'overview' && 'Dashboard Overview'}
              {activeTab === 'hr-management' && 'HR Companies Management'}
              {activeTab === 'course-management' && 'Course Providers Management'}
              {activeTab === 'saas-management' && 'SaaS Providers Management'}
              {activeTab === 'api-keys' && 'API Keys Management'}
              {activeTab === 'transactions' && 'Transactions'}
              {activeTab === 'token-usage' && 'Token Usage Analytics'}
              {activeTab === 'credits' && 'Credit Management'}
              {activeTab === 'demo-leads' && 'Demo Leads'}
              {activeTab === 'phone-test' && 'Phone System Test'}
            </h1>
            <p className="mt-2 text-gray-600">
              {activeTab === 'overview' && 'Platform statistics and quick actions'}
              {activeTab === 'hr-management' && 'Manage HR companies and their access'}
              {activeTab === 'course-management' && 'Manage course providers and offerings'}
              {activeTab === 'saas-management' && 'Manage SaaS providers and services'}
              {activeTab === 'api-keys' && 'Generate and manage API keys for integrations'}
              {activeTab === 'transactions' && 'View all platform transactions'}
              {activeTab === 'token-usage' && 'Monitor AI token consumption and costs'}
              {activeTab === 'credits' && 'Manage user credits and requests'}
              {activeTab === 'demo-leads' && 'Manage demo booking requests'}
              {activeTab === 'phone-test' && 'Test phone call integration'}
            </p>
          </div>

          {/* Stats Overview - Only show on overview tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">HR Companies</p>
                <p className="text-2xl font-bold text-gray-900">{stats.hr_companies}</p>
              </div>
              <Icon name="work" className="text-blue-500 text-3xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Course Providers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.course_providers}</p>
              </div>
              <Icon name="school" className="text-green-500 text-3xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">SaaS Providers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.saas_providers}</p>
              </div>
              <Icon name="cloud" className="text-purple-500 text-3xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active API Keys</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active_api_keys}</p>
              </div>
              <Icon name="key" className="text-yellow-500 text-3xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_jobs}</p>
              </div>
              <Icon name="work" className="text-indigo-500 text-3xl" />
            </div>
              </div>
            </div>
          )}

          {/* Revenue Stats - Only show on overview tab */}
          {activeTab === 'overview' && (
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-8 mb-8 text-white">
              <h2 className="text-2xl font-bold mb-6">Revenue Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-blue-100 text-sm">Total Revenue</p>
                  <p className="text-3xl font-bold">{formatCurrency(revenueStats.total_revenue)}</p>
                </div>
                <div>
                  <p className="text-blue-100 text-sm">Course Revenue</p>
                  <p className="text-2xl font-bold">{formatCurrency(revenueStats.course_revenue)}</p>
                </div>
                <div>
                  <p className="text-blue-100 text-sm">SaaS Revenue</p>
                  <p className="text-2xl font-bold">{formatCurrency(revenueStats.saas_revenue)}</p>
                </div>
                <div>
                  <p className="text-blue-100 text-sm">Commission Earned</p>
                  <p className="text-2xl font-bold">{formatCurrency(revenueStats.total_commission)}</p>
                </div>
              </div>
              <div className="mt-4 text-sm text-blue-100">
                {revenueStats.transactions_count} transactions
              </div>
            </div>
          )}

          {/* Content Area */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Platform Statistics</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded">
                    <span className="text-gray-700">Total HR Companies</span>
                    <span className="font-semibold">{stats.hr_companies}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded">
                    <span className="text-gray-700">Total Course Providers</span>
                    <span className="font-semibold">{stats.course_providers}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded">
                    <span className="text-gray-700">Total SaaS Providers</span>
                    <span className="font-semibold">{stats.saas_providers}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded">
                    <span className="text-gray-700">Active API Keys</span>
                    <span className="font-semibold">{stats.active_api_keys}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded">
                    <span className="text-gray-700">Total Job Postings</span>
                    <span className="font-semibold">{stats.total_jobs}</span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Profile Verification Dashboard */}
                    <div 
                      onClick={() => navigate('/super-admin/verification')}
                      className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                          <Icon name="verified_user" className="text-white text-2xl" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">Profile Verification</h4>
                          <p className="text-xs text-gray-600">Manage & Analytics</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        View verification analytics, configure settings, and search candidate details
                      </p>
                      <div className="flex items-center gap-2 text-blue-600 font-semibold text-sm">
                        <span>Open Dashboard</span>
                        <Icon name="arrow_forward" className="text-lg" />
                      </div>
                    </div>

                    {/* Payment Management */}
                    <div 
                      onClick={() => navigate('/super-admin/payments')}
                      className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center">
                          <Icon name="payments" className="text-white text-2xl" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">Payment Management</h4>
                          <p className="text-xs text-gray-600">Razorpay & Credits</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Manage payments, conversion rates, orders, and webhooks
                      </p>
                      <div className="flex items-center gap-2 text-emerald-600 font-semibold text-sm">
                        <span>Open Dashboard</span>
                        <Icon name="arrow_forward" className="text-lg" />
                      </div>
                    </div>

                    {/* Token Usage */}
                    <div 
                      onClick={() => setActiveTab('token-usage')}
                      className="bg-gradient-to-br from-green-50 to-teal-50 border-2 border-green-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                          <Icon name="analytics" className="text-white text-2xl" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">Token Usage</h4>
                          <p className="text-xs text-gray-600">AI Costs & Stats</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Monitor AI token consumption and costs across all features
                      </p>
                      <div className="flex items-center gap-2 text-green-600 font-semibold text-sm">
                        <span>View Details</span>
                        <Icon name="arrow_forward" className="text-lg" />
                      </div>
                    </div>

                    {/* API Keys */}
                    <div 
                      onClick={() => setActiveTab('api-keys')}
                      className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center">
                          <Icon name="key" className="text-white text-2xl" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">API Keys</h4>
                          <p className="text-xs text-gray-600">Generate & Manage</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Create and manage API keys for external integrations
                      </p>
                      <div className="flex items-center gap-2 text-yellow-600 font-semibold text-sm">
                        <span>Manage Keys</span>
                        <Icon name="arrow_forward" className="text-lg" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cleanup Section */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4">Database Maintenance</h3>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Icon name="warning" className="text-yellow-600 text-xl mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-yellow-800 mb-2">Clean Up Orphaned Content</h4>
                        <p className="text-sm text-yellow-700 mb-4">
                          Remove jobs, courses, and SaaS products from deleted providers. This will clean up content 
                          that's still showing on the website from companies you've already deleted.
                        </p>
                        <Button 
                          onClick={handleCleanupOrphanedContent}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white"
                          disabled={loading}
                        >
                          {loading ? 'Cleaning...' : 'Clean Up Orphaned Content'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* API Keys Tab */}
            {activeTab === 'api-keys' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">API Keys Management</h3>
                    <p className="text-sm text-gray-600 mt-1">Generate and manage API keys for external integrations</p>
                  </div>
                  {!showApiKeyForm && (
                    <Button onClick={() => setShowApiKeyForm(true)} className="flex items-center gap-2">
                      <Icon name="add" />
                      Generate New API Key
                    </Button>
                  )}
                </div>

                {showApiKeyForm && (
                  <div className="bg-white border-2 border-primary/20 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="key" className="text-primary text-xl" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-gray-900">Generate New API Key</h4>
                          <p className="text-sm text-gray-600">Create a secure API key for external integration</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowApiKeyForm(false)}
                        className="text-gray-400 hover:text-gray-600 transition"
                      >
                        <Icon name="close" className="text-2xl" />
                      </button>
                    </div>

                    <form onSubmit={handleCreateApiKey}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            Company Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={apiKeyForm.company_name}
                            onChange={(e) => setApiKeyForm({...apiKeyForm, company_name: e.target.value})}
                            placeholder="e.g., Tech Corp Pvt Ltd"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            CIN Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={apiKeyForm.cin_number}
                            onChange={(e) => setApiKeyForm({...apiKeyForm, cin_number: e.target.value})}
                            placeholder="e.g., U12345AB2020PTC123456"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition"
                            required
                          />
                          <p className="text-xs text-gray-500">Company Identification Number</p>
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            Key Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={apiKeyForm.key_type}
                            onChange={(e) => setApiKeyForm({...apiKeyForm, key_type: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition bg-white"
                            required
                          >
                            <option value="hr">🏢 HR - Job Posting API</option>
                            <option value="course">📚 Course Provider API</option>
                            <option value="saas">☁️ SaaS Provider API</option>
                          </select>
                          <p className="text-xs text-gray-500">Select the type of integration</p>
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            Contact Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={apiKeyForm.contact_email}
                            onChange={(e) => setApiKeyForm({...apiKeyForm, contact_email: e.target.value})}
                            placeholder="contact@company.com"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition"
                            required
                          />
                        </div>

                        <div className="space-y-2 md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            Contact Phone (Optional)
                          </label>
                          <input
                            type="tel"
                            value={apiKeyForm.contact_phone}
                            onChange={(e) => setApiKeyForm({...apiKeyForm, contact_phone: e.target.value})}
                            placeholder="+91-9876543210"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition"
                          />
                        </div>
                      </div>

                      <div className="mt-6 flex gap-3 justify-end">
                        <button
                          type="button"
                          onClick={() => setShowApiKeyForm(false)}
                          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition flex items-center gap-2"
                        >
                          <Icon name="key" />
                          Generate API Key
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {newApiKey && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 mb-6 shadow-lg">
                    {/* Debug info */}
                    <div className="mb-4 p-2 bg-gray-100 rounded text-xs">
                      <strong>Debug:</strong> Key Type: {newApiKey.api_key_info?.key_type || 'unknown'} | 
                      Has Credentials: {newApiKey.provider_credentials ? 'YES ✓' : 'NO ✗'}
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="check_circle" className="text-white text-2xl" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-green-900 mb-2">🎉 API Key Generated Successfully!</h4>
                        <p className="text-sm text-green-700 mb-4">
                          <strong>Important:</strong> Save this API key securely. For security reasons, it won't be shown again.
                        </p>
                        
                        <div className="bg-white rounded-lg border-2 border-green-400 p-4 mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-600 uppercase">Your API Key</span>
                            <span className={`px-2 py-1 text-xs rounded ${
                              (newApiKey.api_key_info?.key_type || newApiKey.key_type) === 'hr' ? 'bg-blue-100 text-blue-700' :
                              (newApiKey.api_key_info?.key_type || newApiKey.key_type) === 'course' ? 'bg-green-100 text-green-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {(newApiKey.api_key_info?.key_type || newApiKey.key_type).toUpperCase()}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded border border-gray-200 font-mono text-sm break-all text-gray-800 select-all">
                            {newApiKey.api_key_info?.api_key || newApiKey.api_key}
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(newApiKey.api_key_info?.api_key || newApiKey.api_key);
                              alert('✓ API key copied to clipboard!');
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                          >
                            <Icon name="content_copy" />
                            Copy API Key
                          </button>
                          <button
                            onClick={() => setNewApiKey(null)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium"
                          >
                            Close
                          </button>
                        </div>

                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs text-blue-800">
                            <strong>Company:</strong> {newApiKey.api_key_info?.company_name || newApiKey.company_name} | 
                            <strong className="ml-2">CIN:</strong> {newApiKey.api_key_info?.cin_number || newApiKey.cin_number} | 
                            <strong className="ml-2">Contact:</strong> {newApiKey.api_key_info?.contact_email || newApiKey.contact_email}
                          </p>
                        </div>

                        {/* Provider Credentials - Show for course and saas */}
                        {newApiKey.provider_credentials && (
                          <div className="mt-4 p-5 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-400 rounded-xl shadow-md">
                            <h5 className="font-bold text-yellow-900 mb-3 flex items-center text-lg">
                              <Icon name="account_circle" className="mr-2 text-2xl" />
                              🔐 Provider Dashboard Login Credentials
                            </h5>
                            <p className="text-sm text-yellow-800 mb-4">
                              Share these credentials with the provider so they can login and manage their content:
                            </p>
                            <div className="bg-white p-4 rounded-lg border-2 border-yellow-300 space-y-3">
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm font-semibold text-gray-700">Username:</span>
                                <span className="font-mono text-base text-primary font-bold">{newApiKey.provider_credentials.username}</span>
                              </div>
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm font-semibold text-gray-700">Password:</span>
                                <span className="font-mono text-base text-primary font-bold">{newApiKey.provider_credentials.password}</span>
                              </div>
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm font-semibold text-gray-700">Email:</span>
                                <span className="text-sm text-gray-900">{newApiKey.provider_credentials.email}</span>
                              </div>
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm font-semibold text-gray-700">Login Page:</span>
                                <a href={newApiKey.provider_credentials.login_url} className="text-sm text-blue-600 hover:underline font-medium">
                                  {window.location.origin}/login
                                </a>
                              </div>
                            </div>
                            <div className="mt-4 flex gap-3">
                              <button
                                onClick={() => {
                                  const credentials = `Username: ${newApiKey.provider_credentials.username}\nPassword: ${newApiKey.provider_credentials.password}\nEmail: ${newApiKey.provider_credentials.email}\nLogin: ${window.location.origin}/login`;
                                  navigator.clipboard.writeText(credentials);
                                  alert('✓ Credentials copied to clipboard!');
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition font-medium"
                              >
                                <Icon name="content_copy" />
                                Copy Credentials
                              </button>
                            </div>
                            <p className="text-xs text-yellow-900 mt-4 font-semibold">
                              ⚠️ IMPORTANT: Save these credentials securely. The provider will need them to access their dashboard.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {apiKeys.length === 0 && !newApiKey ? (
                  <div className="text-center py-20">
                    <div className="w-24 h-24 bg-gradient-to-br from-yellow-50 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon name="key" className="text-yellow-600 text-5xl" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No API Keys Generated</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Generate your first API key to enable external integrations with HR companies, course providers, or SaaS platforms.
                    </p>
                    <Button onClick={() => setShowApiKeyForm(true)} className="inline-flex items-center gap-2 px-6 py-3">
                      <Icon name="add" />
                      Generate Your First API Key
                    </Button>
                    
                    <div className="mt-12 grid grid-cols-3 gap-6 max-w-2xl mx-auto">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <Icon name="work" className="text-blue-600 text-3xl mb-2" />
                        <div className="text-sm font-semibold text-gray-900">HR Integration</div>
                        <div className="text-xs text-gray-600 mt-1">Job posting APIs</div>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <Icon name="school" className="text-green-600 text-3xl mb-2" />
                        <div className="text-sm font-semibold text-gray-900">Course Provider</div>
                        <div className="text-xs text-gray-600 mt-1">Course management</div>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <Icon name="cloud" className="text-purple-600 text-3xl mb-2" />
                        <div className="text-sm font-semibold text-gray-900">SaaS Platform</div>
                        <div className="text-xs text-gray-600 mt-1">Service integration</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {apiKeys.map((key) => (
                      <div key={key.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all">
                        {/* Header Section */}
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-4">
                              <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-md ${
                                key.key_type === 'hr' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                                key.key_type === 'course' ? 'bg-gradient-to-br from-green-500 to-green-600' :
                                'bg-gradient-to-br from-purple-500 to-purple-600'
                              }`}>
                                <Icon name={
                                  key.key_type === 'hr' ? 'work' :
                                  key.key_type === 'course' ? 'school' :
                                  'cloud'
                                } className="text-white text-3xl" />
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 text-xl mb-1">{key.company_name}</h4>
                                <div className="flex items-center gap-2">
                                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                    key.key_type === 'hr' ? 'bg-blue-100 text-blue-700' :
                                    key.key_type === 'course' ? 'bg-green-100 text-green-700' :
                                    'bg-purple-100 text-purple-700'
                                  }`}>
                                    {key.key_type === 'hr' ? '🏢 HR' : key.key_type === 'course' ? '📚 Course' : '☁️ SaaS'}
                                  </span>
                                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                    key.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {key.is_active ? '● Active' : '○ Inactive'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleToggleApiKey(key.id)}
                                className={`px-4 py-2 text-sm font-medium rounded-lg transition shadow-sm ${
                                  key.is_active
                                    ? 'bg-yellow-50 text-yellow-700 border border-yellow-300 hover:bg-yellow-100'
                                    : 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100'
                                }`}
                              >
                                {key.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => handleDeleteApiKey(key.id)}
                                className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-300 rounded-lg hover:bg-red-100 transition shadow-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* API Key Section */}
                        <div className="p-6">
                          <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Icon name="vpn_key" className="text-blue-600" />
                                <span className="text-sm font-semibold text-gray-700 uppercase">API Key</span>
                              </div>
                              <button
                                onClick={() => setVisibleApiKeys({...visibleApiKeys, [key.id]: !visibleApiKeys[key.id]})}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition shadow-sm"
                              >
                                <Icon name={visibleApiKeys[key.id] ? 'visibility_off' : 'visibility'} className="text-sm" />
                                {visibleApiKeys[key.id] ? 'Hide' : 'Show'}
                              </button>
                            </div>
                            <div className="bg-white p-4 rounded-lg border-2 border-blue-300 font-mono text-sm break-all text-gray-800 shadow-inner">
                              {visibleApiKeys[key.id] ? key.api_key : '••••••••••••••••••••••••••••••••••••••••••••••••'}
                            </div>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(key.api_key);
                                alert('✓ API key copied!');
                              }}
                              className="mt-3 flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md"
                            >
                              <Icon name="content_copy" className="text-sm" />
                              Copy API Key
                            </button>
                          </div>

                          {/* Provider Credentials - Show for course/saas */}
                          {key.provider_credentials && (
                            <div className="mb-4 p-5 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl shadow-sm">
                              <h5 className="font-bold text-yellow-900 mb-3 flex items-center text-base">
                                <Icon name="account_circle" className="mr-2 text-xl" />
                                🔐 Provider Dashboard Credentials
                              </h5>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white p-4 rounded-lg border border-yellow-200 shadow-sm">
                                  <p className="text-xs text-gray-500 mb-1 font-medium">Username</p>
                                  <p className="font-mono text-sm font-bold text-primary">{key.provider_credentials.username}</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg border border-yellow-200 shadow-sm">
                                  <p className="text-xs text-gray-500 mb-1 font-medium">Email</p>
                                  <p className="text-sm font-medium text-gray-900">{key.provider_credentials.email}</p>
                                </div>
                              </div>
                              <p className="text-xs text-yellow-800 mt-3 flex items-center gap-1">
                                <Icon name="info" className="text-sm" />
                                Password was shown once during generation. Contact support to reset if needed.
                              </p>
                            </div>
                          )}
                          
                          {/* Other Details */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div>
                              <p className="text-xs text-gray-500 mb-1 font-medium flex items-center gap-1">
                                <Icon name="badge" className="text-xs" />
                                CIN Number
                              </p>
                              <p className="text-sm font-medium text-gray-900">{key.cin_number}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1 font-medium flex items-center gap-1">
                                <Icon name="email" className="text-xs" />
                                Contact Email
                              </p>
                              <p className="text-sm font-medium text-gray-900">{key.contact_email}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1 font-medium flex items-center gap-1">
                                <Icon name="api" className="text-xs" />
                                API Usage
                              </p>
                              <p className="text-sm font-medium text-gray-900">{key.usage_count || 0} requests</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1 font-medium flex items-center gap-1">
                                <Icon name="schedule" className="text-xs" />
                                Created
                              </p>
                              <p className="text-sm font-medium text-gray-900">{formatDate(key.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}



            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div>
                <h3 className="text-lg font-semibold mb-6">Recent Transactions</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transactions.map((tx) => (
                        <tr key={tx.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded ${
                              tx.transaction_type === 'course' ? 'bg-green-100 text-green-700' :
                              tx.transaction_type === 'saas' ? 'bg-purple-100 text-purple-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {tx.transaction_type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-semibold">{formatCurrency(tx.amount)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-green-600">{formatCurrency(tx.commission)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                              {tx.payment_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(tx.transaction_date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {transactions.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No transactions yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Token Usage Tab */}
            {activeTab === 'token-usage' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">AI Token Usage Tracking</h3>
                    <p className="text-sm text-gray-600 mt-1">Monitor AI token consumption and costs across all services</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Current Exchange Rate</p>
                      <p className="text-lg font-bold text-primary">₹{tokenStats.exchange_rate}/USD</p>
                    </div>
                    {!showExchangeRateForm && (
                      <Button onClick={() => setShowExchangeRateForm(true)} className="flex items-center gap-2">
                        <Icon name="edit" />
                        Update Rate
                      </Button>
                    )}
                  </div>
                </div>

                {/* Exchange Rate Update Form */}
                {showExchangeRateForm && (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                    <h4 className="text-lg font-bold text-blue-900 mb-4">Update Exchange Rate</h4>
                    <form onSubmit={handleUpdateExchangeRate} className="flex gap-4 items-end">
                      <div className="flex-1">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          New Rate (INR per USD)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={newExchangeRate}
                          onChange={(e) => setNewExchangeRate(e.target.value)}
                          placeholder="e.g., 90.50"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition"
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowExchangeRateForm(false);
                            setNewExchangeRate('');
                          }}
                          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Token Usage Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                  {/* Overall This Month */}
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="calendar_month" className="text-3xl opacity-80" />
                      <div className="text-right">
                        <p className="text-xs opacity-80">This Month</p>
                        <p className="text-2xl font-bold">{tokenStats.total_tokens_month.toLocaleString()}</p>
                        <p className="text-xs">tokens</p>
                      </div>
                    </div>
                    <div className="border-t border-blue-400 pt-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span>${tokenStats.total_cost_usd_month < 0.01 ? 
                          tokenStats.total_cost_usd_month.toFixed(4) : 
                          tokenStats.total_cost_usd_month.toFixed(2)}</span>
                        <span>₹{tokenStats.total_cost_inr_month < 1 ? 
                          tokenStats.total_cost_inr_month.toFixed(3) : 
                          tokenStats.total_cost_inr_month.toFixed(0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Today */}
                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="today" className="text-3xl opacity-80" />
                      <div className="text-right">
                        <p className="text-xs opacity-80">Today</p>
                        <p className="text-2xl font-bold">{tokenStats.total_tokens_today.toLocaleString()}</p>
                        <p className="text-xs">tokens</p>
                      </div>
                    </div>
                    <div className="border-t border-green-400 pt-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span>${tokenStats.total_cost_usd_today < 0.01 ? 
                          tokenStats.total_cost_usd_today.toFixed(4) : 
                          tokenStats.total_cost_usd_today.toFixed(2)}</span>
                        <span>₹{tokenStats.total_cost_inr_today < 1 ? 
                          tokenStats.total_cost_inr_today.toFixed(3) : 
                          tokenStats.total_cost_inr_today.toFixed(0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Resume Enhancement */}
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="description" className="text-3xl opacity-80" />
                      <div className="text-right">
                        <p className="text-xs opacity-80">Resume Enhance</p>
                        <p className="text-2xl font-bold">{(tokenStats.resume_enhance_usage?.tokens || 0).toLocaleString()}</p>
                        <p className="text-xs">tokens</p>
                      </div>
                    </div>
                    <div className="border-t border-purple-400 pt-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span>${(tokenStats.resume_enhance_usage?.cost_usd || 0) < 0.01 ? 
                          (tokenStats.resume_enhance_usage?.cost_usd || 0).toFixed(4) : 
                          (tokenStats.resume_enhance_usage?.cost_usd || 0).toFixed(2)}</span>
                        <span>₹{(tokenStats.resume_enhance_usage?.cost_inr || 0) < 1 ? 
                          (tokenStats.resume_enhance_usage?.cost_inr || 0).toFixed(3) : 
                          (tokenStats.resume_enhance_usage?.cost_inr || 0).toFixed(0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Interview Usage */}
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="psychology" className="text-3xl opacity-80" />
                      <div className="text-right">
                        <p className="text-xs opacity-80">Interview AI</p>
                        <p className="text-2xl font-bold">{(tokenStats.interview_usage?.tokens || 0).toLocaleString()}</p>
                        <p className="text-xs">tokens</p>
                      </div>
                    </div>
                    <div className="border-t border-orange-400 pt-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span>${(tokenStats.interview_usage?.cost_usd || 0) < 0.01 ? 
                          (tokenStats.interview_usage?.cost_usd || 0).toFixed(4) : 
                          (tokenStats.interview_usage?.cost_usd || 0).toFixed(2)}</span>
                        <span>₹{(tokenStats.interview_usage?.cost_inr || 0) < 1 ? 
                          (tokenStats.interview_usage?.cost_inr || 0).toFixed(3) : 
                          (tokenStats.interview_usage?.cost_inr || 0).toFixed(0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Training Usage */}
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="school" className="text-3xl opacity-80" />
                      <div className="text-right">
                        <p className="text-xs opacity-80">AI Training</p>
                        <p className="text-2xl font-bold">{(tokenStats.training_usage?.tokens || 0).toLocaleString()}</p>
                        <p className="text-xs">tokens</p>
                      </div>
                    </div>
                    <div className="border-t border-indigo-400 pt-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span>${(tokenStats.training_usage?.cost_usd || 0) < 0.01 ? 
                          (tokenStats.training_usage?.cost_usd || 0).toFixed(4) : 
                          (tokenStats.training_usage?.cost_usd || 0).toFixed(2)}</span>
                        <span>₹{(tokenStats.training_usage?.cost_inr || 0) < 1 ? 
                          (tokenStats.training_usage?.cost_inr || 0).toFixed(3) : 
                          (tokenStats.training_usage?.cost_inr || 0).toFixed(0)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Usage Table */}
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900">Detailed Token Usage</h4>
                    <p className="text-sm text-gray-600">Recent AI token consumption with cost breakdown</p>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tokens</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost (USD)</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost (INR)</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tokenDetails.map((detail, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                detail.usage_type === 'interview' ? 'bg-orange-100 text-orange-700' :
                                detail.usage_type === 'training' ? 'bg-indigo-100 text-indigo-700' :
                                detail.usage_type === 'resume_enhance' ? 'bg-purple-100 text-purple-700' :
                                detail.usage_type === 'live_interview_trainer' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {detail.usage_type === 'interview' ? '🎤 Interview' :
                                 detail.usage_type === 'training' ? '🎓 Training' :
                                 detail.usage_type === 'resume_enhance' ? '📄 Resume' :
                                 detail.usage_type === 'live_interview_trainer' ? '🎯 Live Interview' :
                                 detail.usage_type}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {detail.candidate_name || detail.user_name || 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {detail.user_email || ''}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {detail.company_name || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {detail.tokens_used?.toLocaleString() || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                              ${(detail.cost_usd || 0).toFixed(4)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                              ₹{(detail.cost_inr || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {detail.created_at ? formatDate(detail.created_at) : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {tokenDetails.length === 0 && (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="analytics" className="text-gray-400 text-2xl" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Token Usage Yet</h3>
                        <p className="text-gray-600">Token usage will appear here as AI services are used</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* HR Management Tab */}
            {activeTab === 'hr-management' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">HR Companies Management</h3>
                    <p className="text-sm text-gray-600 mt-1">Manage HR companies, their access, and account status</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={loadManagementData}
                      disabled={managementLoading}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition"
                    >
                      <Icon name="refresh" className={managementLoading ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                    <div className="text-sm text-gray-500">
                      Total: {hrCompanies.length} companies
                    </div>
                  </div>
                </div>

                {managementLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading HR companies...</p>
                  </div>
                ) : hrCompanies.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon name="work" className="text-blue-500 text-5xl" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No HR Companies Yet</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      No HR companies have registered on the platform yet. Companies will appear here once they sign up.
                    </p>
                    <div className="flex items-center justify-center gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600">0</div>
                        <div className="text-sm text-gray-500">Total Companies</div>
                      </div>
                      <div className="w-px h-12 bg-gray-300"></div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600">0</div>
                        <div className="text-sm text-gray-500">Active Jobs</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jobs Posted</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {hrCompanies.map((company) => (
                          <tr key={company.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Icon name="work" className="text-blue-600" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{company.company_name}</div>
                                  <div className="text-sm text-gray-500">{company.username}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{company.contact_name}</div>
                              <div className="text-sm text-gray-500">{company.email}</div>
                              <div className="text-sm text-gray-500">{company.phone_number}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {company.jobs_count || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                company.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {company.is_active ? 'Active' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(company.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleToggleUserAccess(company.id, 'hr', company.is_active)}
                                  className={`px-3 py-1 text-xs rounded ${
                                    company.is_active
                                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                >
                                  {company.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(company.id, 'HR Company')}
                                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Course Providers Management Tab */}
            {activeTab === 'course-management' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Course Providers Management</h3>
                    <p className="text-sm text-gray-600 mt-1">Manage course providers, their access, and course offerings</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={loadManagementData}
                      disabled={managementLoading}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition"
                    >
                      <Icon name="refresh" className={managementLoading ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                    <div className="text-sm text-gray-500">
                      Total: {courseProviders.length} providers
                    </div>
                  </div>
                </div>

                {managementLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading course providers...</p>
                  </div>
                ) : courseProviders.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-24 h-24 bg-gradient-to-br from-green-50 to-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon name="school" className="text-green-500 text-5xl" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No Course Providers Yet</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      No course providers have registered on the platform yet. Providers will appear here once they sign up.
                    </p>
                    <div className="flex items-center justify-center gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600">0</div>
                        <div className="text-sm text-gray-500">Total Providers</div>
                      </div>
                      <div className="w-px h-12 bg-gray-300"></div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600">0</div>
                        <div className="text-sm text-gray-500">Active Courses</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Courses</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {courseProviders.map((provider) => (
                          <tr key={provider.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <Icon name="school" className="text-green-600" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{provider.provider_name}</div>
                                  <div className="text-sm text-gray-500">{provider.username}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{provider.contact_name}</div>
                              <div className="text-sm text-gray-500">{provider.email}</div>
                              <div className="text-sm text-gray-500">{provider.phone}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {provider.courses_count || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                provider.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {provider.is_active ? 'Active' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(provider.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleToggleUserAccess(provider.id, 'course_provider', provider.is_active)}
                                  className={`px-3 py-1 text-xs rounded ${
                                    provider.is_active
                                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                >
                                  {provider.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(provider.id, 'Course Provider')}
                                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* SaaS Providers Management Tab */}
            {activeTab === 'saas-management' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">SaaS Providers Management</h3>
                    <p className="text-sm text-gray-600 mt-1">Manage SaaS providers, their access, and service offerings</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={loadManagementData}
                      disabled={managementLoading}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 transition"
                    >
                      <Icon name="refresh" className={managementLoading ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                    <div className="text-sm text-gray-500">
                      Total: {saasProviders.length} providers
                    </div>
                  </div>
                </div>

                {managementLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading SaaS providers...</p>
                  </div>
                ) : saasProviders.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-24 h-24 bg-gradient-to-br from-purple-50 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon name="cloud" className="text-purple-500 text-5xl" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No SaaS Providers Yet</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      No SaaS providers have registered on the platform yet. Providers will appear here once they sign up.
                    </p>
                    <div className="flex items-center justify-center gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-purple-600">0</div>
                        <div className="text-sm text-gray-500">Total Providers</div>
                      </div>
                      <div className="w-px h-12 bg-gray-300"></div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-indigo-600">0</div>
                        <div className="text-sm text-gray-500">Active Services</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Services</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {saasProviders.map((provider) => (
                          <tr key={provider.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                  <Icon name="cloud" className="text-purple-600" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{provider.provider_name}</div>
                                  <div className="text-sm text-gray-500">{provider.username}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{provider.contact_name}</div>
                              <div className="text-sm text-gray-500">{provider.email}</div>
                              <div className="text-sm text-gray-500">{provider.phone}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {provider.services_count || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                provider.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {provider.is_active ? 'Active' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(provider.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleToggleUserAccess(provider.id, 'saas_provider', provider.is_active)}
                                  className={`px-3 py-1 text-xs rounded ${
                                    provider.is_active
                                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                >
                                  {provider.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(provider.id, 'SaaS Provider')}
                                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Demo Leads Tab */}
            {activeTab === 'demo-leads' && (
              <div>
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Demo Leads Management</h3>
                  <p className="text-sm text-gray-600 mt-1">Manage demo booking requests from potential clients</p>
                </div>
                <DemoLeadsManager adminId={adminId} />
              </div>
            )}

            {/* Phone Test Tab */}
            {activeTab === 'phone-test' && (
              <div>
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">📞 Phone System Test</h3>
                  <p className="text-sm text-gray-600 mt-1">Test phone call integration with AI interview system</p>
                </div>

                {/* Phone System Status */}
                {phoneSystemStatus && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-3">System Status</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className={`p-3 rounded ${phoneSystemStatus.phone_system_ready ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        <div className="font-medium">Phone System</div>
                        <div>{phoneSystemStatus.phone_system_ready ? '✅ Ready' : '❌ Not Ready'}</div>
                      </div>
                      <div className={`p-3 rounded ${phoneSystemStatus.twilio_configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        <div className="font-medium">Twilio Config</div>
                        <div>{phoneSystemStatus.twilio_configured ? '✅ Configured' : '⚠️ Missing'}</div>
                      </div>
                      <div className={`p-3 rounded ${phoneSystemStatus.twilio_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        <div className="font-medium">Twilio Package</div>
                        <div>{phoneSystemStatus.twilio_available ? '✅ Available' : '❌ Not Installed'}</div>
                      </div>
                      <div className={`p-3 rounded ${phoneSystemStatus.pipecat_available ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        <div className="font-medium">Audio Mode</div>
                        <div>{phoneSystemStatus.audio_mode || (phoneSystemStatus.pipecat_available ? '✅ Gemini Live' : '⚠️ STT/TTS')}</div>
                      </div>
                    </div>
                    {phoneSystemStatus.webhook_url && (
                      <div className="mt-3 p-3 bg-blue-50 rounded">
                        <div className="text-sm font-medium text-blue-800">Webhook URL:</div>
                        <div className="text-xs text-blue-600 font-mono">{phoneSystemStatus.webhook_url}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Test Call Form */}
                <div className="bg-white border rounded-lg p-6">
                  <h4 className="font-medium mb-4">Make Test Call</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number to Call
                      </label>
                      <Input
                        type="tel"
                        value={phoneTestNumber}
                        onChange={(e) => setPhoneTestNumber(e.target.value)}
                        placeholder="+919176186062"
                        className="w-full max-w-md"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Include country code (e.g., +91 for India, +1 for US)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Language / மொழி / भाषा
                      </label>
                      <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="w-full max-w-md p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="en-US">🇺🇸 English (US)</option>
                        <option value="en-IN">🇮🇳 English (India)</option>
                        <option value="ta-IN">🇮🇳 தமிழ் (Tamil)</option>
                        <option value="hi-IN">🇮🇳 हिंदी (Hindi)</option>
                        <option value="te-IN">🇮🇳 తెలుగు (Telugu)</option>
                        <option value="ml-IN">🇮🇳 മലയാളം (Malayalam)</option>
                        <option value="kn-IN">🇮🇳 ಕನ್ನಡ (Kannada)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select the language for AI voice and speech recognition
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom AI Prompt (Optional)
                      </label>
                      <textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Enter custom instructions for the AI call agent... (Leave empty for default interview prompt)"
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                        rows={4}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Example: "Act as a friendly customer service agent helping with product inquiries" or "Conduct a technical interview for a Python developer role"
                      </p>
                    </div>

                    <div className="flex gap-4">
                      <Button
                        onClick={handleTestPhoneCall}
                        disabled={phoneTestLoading || !phoneTestNumber}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {phoneTestLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Making Call...
                          </>
                        ) : (
                          <>
                            📞 Make Test Call
                          </>
                        )}
                      </Button>

                      <Button
                        onClick={loadPhoneSystemStatus}
                        variant="outline"
                      >
                        🔄 Refresh Status
                      </Button>

                      <Button
                        onClick={() => setCustomPrompt('')}
                        variant="outline"
                        disabled={!customPrompt}
                      >
                        🗑️ Clear Prompt
                      </Button>
                    </div>

                    {/* Preset Prompts */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quick Preset Prompts
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setCustomPrompt("You are a friendly AI assistant conducting a brief phone interview. Ask about the candidate's background, experience, and career goals. Keep it conversational and encouraging.")}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200"
                        >
                          📋 General Interview
                        </button>
                        <button
                          onClick={() => setCustomPrompt("You are a technical recruiter conducting a phone screening for a software developer position. Ask about programming languages, projects, and technical experience. Be professional but friendly.")}
                          className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded-full hover:bg-green-200"
                        >
                          💻 Tech Interview
                        </button>
                        <button
                          onClick={() => setCustomPrompt("You are a customer service representative helping with product inquiries. Be helpful, patient, and provide clear information about services.")}
                          className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
                        >
                          🎧 Customer Service
                        </button>
                        <button
                          onClick={() => setCustomPrompt("You are a sales representative making a follow-up call. Be enthusiastic, ask about their needs, and explain how your services can help them.")}
                          className="px-3 py-1 text-xs bg-orange-100 text-orange-800 rounded-full hover:bg-orange-200"
                        >
                          📈 Sales Call
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Test Result */}
                  {phoneTestResult && (
                    <div className={`mt-4 p-4 rounded-lg ${phoneTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <h5 className={`font-medium ${phoneTestResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {phoneTestResult.success ? '✅ Call Initiated Successfully' : '❌ Call Failed'}
                      </h5>
                      {phoneTestResult.success ? (
                        <div className="mt-2 text-sm text-green-700">
                          <p><strong>Call SID:</strong> {phoneTestResult.call_sid}</p>
                          <p><strong>To:</strong> {phoneTestResult.to}</p>
                          <p><strong>From:</strong> {phoneTestResult.from}</p>
                          <p><strong>Status:</strong> {phoneTestResult.status}</p>
                          {phoneTestResult.custom_prompt && phoneTestResult.custom_prompt !== 'Using default prompt' && (
                            <p><strong>Custom Prompt:</strong> Applied ✅</p>
                          )}
                          <p className="mt-2 font-medium">Your phone should ring shortly! Answer to test the AI conversation.</p>
                        </div>
                      ) : (
                        <div className="mt-2 text-sm text-red-700">
                          <p><strong>Error:</strong> {phoneTestResult.error}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Instructions */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-medium text-blue-800 mb-2">How it works:</h5>
                    <ol className="text-sm text-blue-700 space-y-1">
                      <li>1. Enter your phone number above</li>
                      <li>2. (Optional) Add a custom prompt to control how the AI behaves</li>
                      <li>3. Click "Make Test Call" button</li>
                      <li>4. Your phone will ring within a few seconds</li>
                      <li>5. Answer the call to talk with the AI agent</li>
                      <li>6. The AI will follow your custom prompt or use default interview behavior</li>
                    </ol>
                    <div className="mt-3 p-3 bg-blue-100 rounded">
                      <p className="text-xs text-blue-800 font-medium">💡 Custom Prompt Tips:</p>
                      <ul className="text-xs text-blue-700 mt-1 space-y-1">
                        <li>• Be specific about the role (interviewer, customer service, sales, etc.)</li>
                        <li>• Include tone instructions (friendly, professional, enthusiastic)</li>
                        <li>• Mention what topics to cover or avoid</li>
                        <li>• Keep it under 500 characters for best results</li>
                      </ul>
                    </div>
                    <p className="text-xs text-blue-600 mt-3">
                      <strong>Note:</strong> This uses your existing live interview system with phone call integration.
                      Calls are powered by Twilio and AI responses by Gemini.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Credits Management Tab */}
            {activeTab === 'credits' && (
              <div>
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">💰 Credit Management</h3>
                  <p className="text-sm text-gray-600 mt-1">Manage user credits, requests, and settings</p>
                </div>
                <CreditManagement />
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SuperAdminDashboard;
